import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import cn from '../../utils/cn';

/**
 * Accessible dialog: focus trap, Escape to close, focus restored on unmount,
 * and a bottom-sheet presentation on small screens (handled in CSS).
 */
export default function Modal({ open, onClose, title, subtitle, icon, width = 520, children, footer, className, closeOnScrim = true }) {
  const ref = useRef(null);
  const restoreTo = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    restoreTo.current = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    const node = ref.current;
    const focusables = () =>
      node?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || [];
    setTimeout(() => {
      const first = [...focusables()].find((el) => !el.disabled && el.type !== 'hidden');
      (first || node)?.focus();
    }, 20);

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const list = [...focusables()].filter((el) => !el.disabled && el.offsetParent !== null);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = overflow;
      restoreTo.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="scrim"
      onMouseDown={(e) => {
        if (closeOnScrim && e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={ref}
        className={cn('modal', className)}
        style={{ '--modal-w': `${width}px` }}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
      >
        {(title || icon) && (
          <div className="modal__header">
            {icon}
            <div style={{ minWidth: 0 }}>
              <div className="modal__title">{title}</div>
              {subtitle && <div className="modal__sub">{subtitle}</div>}
            </div>
            <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
              <X size={17} />
            </button>
          </div>
        )}
        <div className="modal__body">{children}</div>
        {footer && <div className="modal__footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
